import { useEffect, useRef } from 'react';
import { formatDistanceToNow } from 'date-fns';

export interface ChatMessage {
  chatMessageId: number;
  workOrderId: number;
  senderStaffId: number;
  senderName: string;
  senderRole: string;
  messageText: string;
  sentAt: string;
  isRead: boolean;
  readAt?: string;
  deliveredAt?: string;
}

export interface TypingIndicator {
  senderName: string;
}

interface ChatThreadProps {
  messages: ChatMessage[];
  currentStaffId: number | null;
  loading?: boolean;
  emptyText?: string;
  isTyping?: TypingIndicator | null;
}

export function ChatThread({ messages, currentStaffId, loading, emptyText, isTyping }: ChatThreadProps) {
  const bottomRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages.length]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-32 text-slate-400 text-sm">
        Loading messages…
      </div>
    );
  }

  if (messages.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-32 text-slate-400 text-sm gap-2">
        <span className="material-symbols-outlined text-3xl">chat</span>
        {emptyText ?? 'No messages yet. Start the conversation!'}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3 p-4">
      {messages.map((msg) => {
        const isMe = msg.senderStaffId === currentStaffId;
        const time = formatDistanceToNow(new Date(msg.sentAt), { addSuffix: true });

        return (
          <div key={msg.chatMessageId} className={`flex ${isMe ? 'justify-end' : 'justify-start'}`}>
            <div className={`flex flex-col gap-0.5 max-w-[75%] ${isMe ? 'items-end' : 'items-start'}`}>
              {!isMe && (
                <span className="text-[11px] font-medium text-slate-500 dark:text-slate-400 px-1">
                  {msg.senderName}
                  {msg.senderRole ? ` · ${msg.senderRole}` : ''}
                </span>
              )}
              <div
                className={`px-3 py-2 rounded-2xl text-sm leading-relaxed ${
                  isMe
                    ? 'bg-primary-600 text-white rounded-br-sm'
                    : 'bg-slate-100 dark:bg-white/10 text-slate-900 dark:text-slate-100 rounded-bl-sm'
                }`}
              >
                {msg.messageText}
              </div>
              <div className="flex items-center gap-1.5 px-1">
                <span className="text-[10px] text-slate-400">{time}</span>
                {isMe && (
                  <span className="text-[10px] text-slate-400 flex items-center gap-0.5">
                    {msg.isRead ? (
                      <>
                        <span>✓✓</span>
                        <span className="text-primary-600">read</span>
                      </>
                    ) : msg.deliveredAt ? (
                      <>
                        <span>✓✓</span>
                        <span>sent</span>
                      </>
                    ) : (
                      <span>✓</span>
                    )}
                  </span>
                )}
              </div>
            </div>
          </div>
        );
      })}
      {isTyping && (
        <div className="flex justify-start">
          <div className="flex flex-col gap-0.5 max-w-[75%]">
            <span className="text-[11px] font-medium text-slate-500 dark:text-slate-400 px-1">
              {isTyping.senderName}
            </span>
            <div className="px-3 py-2 rounded-2xl bg-slate-100 dark:bg-white/10 rounded-bl-sm flex items-center gap-1">
              <span className="text-[10px] text-slate-600 dark:text-slate-400 italic">{isTyping.senderName} is typing</span>
              <span className="flex gap-0.5">
                <span className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                <span className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                <span className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
              </span>
            </div>
          </div>
        </div>
      )}
      <div ref={bottomRef} />
    </div>
  );
}
